import { useEffect, useLayoutEffect, useRef } from 'react';

// Compensates a focus underline drawn outside the measured bottomRow; otherwise it gets clipped.
const FOCUS_UNDERLINE_HEIGHT_PX = 2;

export const useAutosizedTextareaBox = <T>(dep: T, minFraction = 0.33) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const bottomRowRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const recompute = () => {
    const ta = textareaRef.current;
    const container = containerRef.current;
    const box = boxRef.current;
    const bottomRow = bottomRowRef.current;
    if (!ta || !container || !box || !bottomRow) return;

    // Reset both so the textarea's scrollHeight reflects its intrinsic content (not the previous fixed box height).
    box.style.height = 'auto';
    ta.style.height = 'auto';

    let siblingHeight = 0;
    for (const child of Array.from(container.children)) {
      if (child !== box) siblingHeight += (child as HTMLElement).offsetHeight;
    }
    const cs = getComputedStyle(container);
    const paddingY = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
    const gap = parseFloat(cs.rowGap || '0');
    const totalGap = Math.max(0, container.children.length - 1) * gap;
    const available = container.clientHeight - paddingY - totalGap - siblingHeight;

    const desired = ta.scrollHeight + bottomRow.offsetHeight + FOCUS_UNDERLINE_HEIGHT_PX;
    const target = Math.max(available * minFraction, Math.min(desired, available));
    box.style.height = `${target}px`;
  };

  useLayoutEffect(() => {
    recompute();
  }, [dep]);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(recompute);
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  return { containerRef, boxRef, bottomRowRef, textareaRef };
};
